import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Attachment } from 'fhir/r4';
import { MatButtonModule } from '@angular/material/button';
import { attachmentToDataUrl } from '../signature-extension.utils';

@Component({
  selector: 'app-signature-canvas',
  standalone: true,
  imports: [CommonModule, MatButtonModule],
  template: `
    <div class="signature-wrapper">
      <canvas
        #signatureCanvas
        class="signature-canvas"
        width="1000"
        height="320"
        (pointerdown)="onPointerDown($event)"
        (pointermove)="onPointerMove($event)"
        (pointerup)="onPointerUp()"
        (pointercancel)="onPointerUp()"
        (pointerleave)="onPointerUp()"></canvas>

      <div class="signature-actions">
        <button mat-stroked-button type="button" (click)="clearSignature()">Clear</button>
      </div>
    </div>
  `,
  styles: [`
    .signature-wrapper {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .signature-canvas {
      width: 100%;
      max-width: 100%;
      min-height: 180px;
      border: 1px dashed var(--mat-sys-outline-variant);
      border-radius: 8px;
      background-color: #fff;
      touch-action: none;
      cursor: crosshair;
    }

    .signature-actions {
      display: flex;
      justify-content: flex-end;
    }
  `]
})
export class SignatureCanvasComponent implements AfterViewInit {
  @ViewChild('signatureCanvas', { static: true })
  private readonly canvasRef!: ElementRef<HTMLCanvasElement>;

  @Input() initialAttachment: Attachment | null = null;
  @Output() signatureChange = new EventEmitter<Attachment | null>();

  private isDrawing = false;

  ngAfterViewInit(): void {
    this.initializeCanvas();
    this.drawInitialSignature();
  }

  onPointerDown(event: PointerEvent): void {
    const { x, y } = this.getCanvasPoint(event);
    const context = this.getContext();
    context.beginPath();
    context.moveTo(x, y);
    this.isDrawing = true;
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.isDrawing) {
      return;
    }
    const { x, y } = this.getCanvasPoint(event);
    const context = this.getContext();
    context.lineTo(x, y);
    context.stroke();
  }

  onPointerUp(): void {
    if (!this.isDrawing) {
      return;
    }
    this.isDrawing = false;
    this.emitCanvasAsAttachment();
  }

  clearSignature(): void {
    const canvas = this.canvasRef.nativeElement;
    const context = this.getContext();
    context.clearRect(0, 0, canvas.width, canvas.height);
    this.initializeCanvas();
    this.signatureChange.emit(null);
  }

  private initializeCanvas(): void {
    const context = this.getContext();
    context.lineWidth = 2;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = '#111827';
  }

  private drawInitialSignature(): void {
    const dataUrl = attachmentToDataUrl(this.initialAttachment ?? undefined);
    if (!dataUrl) {
      return;
    }

    const image = new Image();
    image.onload = () => {
      const canvas = this.canvasRef.nativeElement;
      const context = this.getContext();
      context.clearRect(0, 0, canvas.width, canvas.height);

      const scale = Math.min(canvas.width / image.width, canvas.height / image.height);
      const drawWidth = image.width * scale;
      const drawHeight = image.height * scale;
      const offsetX = (canvas.width - drawWidth) / 2;
      const offsetY = (canvas.height - drawHeight) / 2;

      context.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);
      this.initializeCanvas();
    };
    image.src = dataUrl;
  }

  private emitCanvasAsAttachment(): void {
    const canvas = this.canvasRef.nativeElement;
    const dataUrl = canvas.toDataURL('image/png');
    const base64 = dataUrl.split(',')[1];
    if (!base64) {
      this.signatureChange.emit(null);
      return;
    }

    this.signatureChange.emit({
      contentType: 'image/png',
      data: base64,
      creation: new Date().toISOString()
    });
  }

  private getCanvasPoint(event: PointerEvent): { x: number; y: number } {
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY
    };
  }

  private getContext(): CanvasRenderingContext2D {
    return this.canvasRef.nativeElement.getContext('2d') as CanvasRenderingContext2D;
  }
}
